import styled from "styled-components";

import { getTheme, themed, Flow, FlexBox, FlexFlow, Text, type SwatchList, } from "@bracket/ui";
import { usePlatform, } from "@bracket/platform/store";
import { useEffect, useRef,useState, } from "react";
import { Cell, Pie, PieChart as RechartsPieChart, ResponsiveContainer, Tooltip, } from "recharts";

import { BrktTVL, } from "./BrktTVL";
 
export declare type PieChartProps = {
	height?: number;
	labelsInside?: boolean;
	innerRadius?: number;
	outerRadius?: number;
	title?: string;
	swatch?: SwatchList;
}


export const PieChart = ({
	height = 400,
	labelsInside = false,
	innerRadius = 63,
	outerRadius = 74,
	swatch = "swatch_one",
}: PieChartProps) => {
	themed();

	const { ready, colateralBalances, } = usePlatform();
	const [ hasAnimated, setHasAnimated, ] = useState(false);
	const inte = useRef<NodeJS.Timeout|null>(null);

	const data = colateralBalances.map((balance: any) => ({
		title: balance.title,
		value: balance.count,
		originalValue: balance.count,
		percentage: balance.completePercentage,
	}));

	useEffect(() => {
		if(ready && colateralBalances && !hasAnimated && !inte.current) {
			inte.current = setTimeout(() => {
				setHasAnimated(true);
			}, 3500);
		}
	}, [ ready, colateralBalances, hasAnimated, inte.current, ]);

	const renderCustomTooltip = ({ active, payload }: any) => {
		if (active && payload && payload.length) {
			const { title, originalValue, percentage } = payload[0].payload;
			return (
				<CustomTooltip>
					<TooltipLabel>{title}</TooltipLabel>
					<TooltipValue>
						{originalValue.toFixed(5)} ETH ({percentage.toFixed(2)}%)
					</TooltipValue>
				</CustomTooltip>
			);
		}
		return null;
	};

	const renderCustomLabel = ({
		cx,
		cy,
		midAngle,
		innerRadius,
		outerRadius,
		title,
		payload,
	}: any) => {
		const RADIAN = Math.PI / 180;
		const radius = labelsInside 
			? innerRadius + (outerRadius - innerRadius) * 0.5
			: innerRadius + (outerRadius - innerRadius) * 1.6;
		const x = cx + radius * Math.cos(-midAngle * RADIAN);
		const y = cy + radius * Math.sin(-midAngle * RADIAN);

		return (
			<g>
				<text
					x={x}
					y={y}
					textAnchor={x > cx ? "start" : "end"}
					dominantBaseline="central"
					style={{
						fontFamily: getTheme().fonts.numeric,
						fontSize: "14px",
						lineHeight: "20px",
						fontWeight: 400,
						fill: getTheme().paragraph,
					}}
				>
					{`${title} ${payload.originalValue.toFixed(2)} ETH`}
				</text>
			</g>
		);
	};

	if(!ready) {
		return (
			<ChartContainer style={{ height }}>
				<Text color={getTheme().heading}>
					Loading treasury balances...
				</Text>
			</ChartContainer>
		);
	}

	if(!colateralBalances) {
		return (
			<ChartContainer style={{ height }}>
				<ChartTitle variant="p" color={getTheme().heading}>
					Error loading treasury balance22s
				</ChartTitle>
				
			</ChartContainer>
		);
	}

	return (
		<ChartContainer style={{ height }}>
			<ChartHeader>
				<Text h5 color="heading">Treasury balances</Text>
				<BrktTVL />
			</ChartHeader>
			<ChartWrapper>
				<ResponsiveContainer width="100%" height={height}>
					<RechartsPieChart>
						<Pie
							data={data}
							dataKey="value"
							nameKey="title"
							cx="50%"
							cy="50%"
							innerRadius={`${innerRadius}%`}
							outerRadius={`${outerRadius}%`}
							paddingAngle={2}
							cornerRadius={20}
							label={renderCustomLabel}
							labelLine={false}
							isAnimationActive={!hasAnimated}>
							{data.map(({ title }: any, index: number) => (
								<Cell key={`cell-${title}`}
									fill={getTheme()[swatch][index % getTheme()[swatch].length]}
									stroke={getTheme().surface_on}
									strokeWidth={1}
								/>
							))}
						</Pie>
						<Tooltip content={renderCustomTooltip} />
					</RechartsPieChart>
				</ResponsiveContainer>
			</ChartWrapper>
		</ChartContainer>
	);
};


const ChartContainer = styled(FlexFlow)`
  width: 100%;
  height: 100%;
  align-items: center;
  justify-content: center;
`;

const ChartHeader = styled(Flow)`
	justify-content: space-between;
	align-items: center;
`;

const ChartTitle = styled(Text)`
	text-align: center;
	margin-bottom: 24px;
`;

const ChartWrapper = styled(FlexBox)`
	flex: 1;
	width: 100%;
	align-items: center;
	justify-content: center;
	* {
		outline: none!important;
		user-select: none!important;
	}
`;

const CustomTooltip = styled.div`
	background: ${() => getTheme().surface_container};
	border: 1px solid ${() => getTheme().outline};
	border-radius: 4px;
	padding: 8px 12px;
	font-family: ${() => getTheme().fonts.body};
`;

const TooltipLabel = styled.p`
	color: ${() => getTheme().heading};
	font-size: 14px;
	line-height: 20px;
	margin: 0;
`;

const TooltipValue = styled.p`
	color: ${() => getTheme().paragraph};
	font-size: 12px;
	line-height: 16px;
	margin: 4px 0 0;
`;
